import React, { PropsWithChildren, useEffect, useRef } from "react";

type UnderConstructionOverlayProps = PropsWithChildren<{
  title?: string;
  message?: string;
}>;

const ConstructionPylonIcon = () => (
  <svg
    className="h-16 w-16"
    viewBox="0 0 96 96"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M36 16h24l13 56H23l13-56Z"
      fill="#F97316"
      stroke="#9A3412"
      strokeWidth="4"
      strokeLinejoin="round"
    />
    <path
      d="M31.5 39h33M27.5 57h41"
      stroke="#FFF7ED"
      strokeWidth="9"
      strokeLinecap="round"
    />
    <path
      d="M18 72h60l6 12H12l6-12Z"
      fill="#7C2D12"
      stroke="#431407"
      strokeWidth="4"
      strokeLinejoin="round"
    />
    <path
      d="M41 16h14l3 14H38l3-14Z"
      fill="#FDBA74"
      opacity="0.85"
    />
  </svg>
);

const UnderConstructionOverlay = ({
  children,
  title = "Currently Being Updated",
  message = "This page is still being prepared. Other Chamber Member Pro pages are available to use, so please continue using the features that are already open in your dashboard.",
}: UnderConstructionOverlayProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const contentElement = contentRef.current;
    const previousOverflow = document.body.style.overflow;
    const scrollContainers: Array<{
      element: HTMLElement;
      overflow: string;
    }> = [];

    let parentElement = rootRef.current?.parentElement;
    while (parentElement) {
      const styles = window.getComputedStyle(parentElement);
      const scrollsContent =
        parentElement.scrollHeight > parentElement.clientHeight;
      const canScroll = /(auto|scroll|overlay)/.test(
        `${styles.overflow} ${styles.overflowY}`
      );

      if (scrollsContent && canScroll) {
        scrollContainers.push({
          element: parentElement,
          overflow: parentElement.style.overflow,
        });
        parentElement.style.overflow = "hidden";
      }

      parentElement = parentElement.parentElement;
    }

    document.body.style.overflow = "hidden";
    contentElement?.setAttribute("inert", "");

    return () => {
      document.body.style.overflow = previousOverflow;
      contentElement?.removeAttribute("inert");
      scrollContainers.forEach(({ element, overflow }) => {
        element.style.overflow = overflow;
      });
    };
  }, []);

  return (
    <div ref={rootRef} className="relative min-h-[calc(100vh-12rem)]">
      <div
        ref={contentRef}
        className="pointer-events-none select-none blur-lg"
        aria-hidden="true"
      >
        {children}
      </div>

      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-gradient-to-br from-white/75 via-slate-100/70 to-slate-300/45 px-4 backdrop-blur-xl">
        <div
          className="mx-auto flex w-full max-w-md flex-col items-center rounded-3xl border border-white/70 bg-white/95 p-8 text-center shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="under-construction-title"
          aria-describedby="under-construction-message"
        >
          <div className="mb-5 rounded-full bg-orange-100 p-4 text-orange-500 shadow-inner">
            <ConstructionPylonIcon />
          </div>
          <h2
            id="under-construction-title"
            className="text-2xl font-bold text-gray-900"
          >
            {title}
          </h2>
          <p
            id="under-construction-message"
            className="mt-3 text-base text-gray-600"
          >
            {message}
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnderConstructionOverlay;
